/**
 * <variant-picker product-handle="...">
 *
 * Listens for radio change events inside, picks the matching variant from
 * the embedded JSON payload, and updates the form variant ID, price,
 * availability state, browser URL and main media element.
 */
(() => {
  const moneyFormat = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

  function formatMoney(cents) {
    const value = Number(cents) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: moneyFormat,
      }).format(value);
    } catch (e) {
      return value.toFixed(2);
    }
  }

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataNode = this.querySelector('script[type="application/json"][data-variants]');
      if (!dataNode) return;

      try {
        this.variants = JSON.parse(dataNode.textContent);
      } catch (e) {
        return;
      }

      this.section = this.closest('[data-product-section]') || document;
      this.form = this.section.querySelector('form[data-product-form]');
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.priceTarget = this.section.querySelector('[data-product-price]');
      this.compareTarget = this.section.querySelector('[data-product-compare-price]');
      this.cta = this.section.querySelector('[data-add-to-cart]');
      this.ctaLabel = this.cta && this.cta.querySelector('[data-cta-label]');
      this.mtmCta = this.section.querySelector('[data-mtm-request]');
      this.media = this.section.querySelector('[data-product-media]');

      this.addEventListener('change', this.onChange.bind(this));
      this.updateStateFromInputs();
    }

    onChange() {
      this.updateStateFromInputs();
    }

    updateStateFromInputs() {
      const selected = Array.from(this.querySelectorAll('input[type="radio"]:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);

      if (!selected.length || !this.variants) return;

      /* A variant named "Maßanfertigung" swaps the cart button for the
         enquiry link. */
      const isMadeToMeasure = selected.some((value) =>
        value.toLowerCase().includes('maßanfertigung')
      );
      if (this.mtmCta && this.cta) {
        this.mtmCta.hidden = !isMadeToMeasure;
        this.cta.hidden = isMadeToMeasure;
      }

      const variant = this.variants.find((v) =>
        v.options.length === selected.length &&
        v.options.every((value, idx) => value === selected[idx])
      );

      this.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
      );

      if (!variant) {
        this.toggleAvailability(false, this.unavailableLabel());
        return;
      }

      // Form id
      if (this.idInput) this.idInput.value = variant.id;

      // Price
      if (this.priceTarget) {
        this.priceTarget.textContent = formatMoney(variant.price);
        this.priceTarget.classList.toggle(
          'product-price__current--sale',
          variant.compare_at_price && variant.compare_at_price > variant.price
        );
      }
      if (this.compareTarget) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.compareTarget.textContent = formatMoney(variant.compare_at_price);
          this.compareTarget.hidden = false;
        } else {
          this.compareTarget.hidden = true;
        }
      }

      // CTA availability
      this.toggleAvailability(variant.available, variant.available ? this.availableLabel() : this.soldOutLabel());

      // URL
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } catch (e) { /* noop */ }

      // Media swap — scroll the gallery to the variant's featured media
      if (variant.featured_media && this.media) {
        const mediaItem = this.media.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (mediaItem) {
          mediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      }
    }

    toggleAvailability(available, label) {
      if (this.cta) {
        this.cta.toggleAttribute('disabled', !available);
        this.cta.setAttribute('aria-disabled', !available);
      }
      if (this.ctaLabel && label) this.ctaLabel.textContent = label;
    }

    availableLabel() { return this.dataset.labelAvailable || 'In den Warenkorb'; }
    soldOutLabel() { return this.dataset.labelSoldOut || 'Ausverkauft'; }
    unavailableLabel() { return this.dataset.labelUnavailable || 'Nicht verfügbar'; }
  }

  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', VariantPicker);
  }
})();

/**
 * <product-media-counter target="...">
 * Watches a scroll-snap gallery and shows "1 / N" indicator on mobile.
 */
(() => {
  class ProductMediaCounter extends HTMLElement {
    connectedCallback() {
      const targetId = this.getAttribute('target');
      this.gallery = targetId ? document.getElementById(targetId) : null;
      if (!this.gallery) return;

      this.items = this.gallery.querySelectorAll('.product-media__item');
      if (!this.items.length) return;

      this.total = this.items.length;
      this.current = 1;
      this.render();

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const idx = Array.prototype.indexOf.call(this.items, entry.target);
            if (idx >= 0) {
              this.current = idx + 1;
              this.render();
            }
          }
        });
      }, { root: this.gallery, threshold: [0.5] });

      this.items.forEach((item) => io.observe(item));
    }

    render() {
      this.textContent = `${this.current} / ${this.total}`;
    }
  }

  if (!customElements.get('product-media-counter')) {
    customElements.define('product-media-counter', ProductMediaCounter);
  }
})();
